/**
 * Dynasty valuation: what a rookie is worth over the seasons you will hold him
 * (§12, "keeper/dynasty valuation and rookie-pick curves").
 *
 * Redraft asks how many points a player scores this season. Dynasty asks for
 * the discounted sum over a holding period, which needs two things redraft does
 * not: an expectation that already contains bust risk, and a model of how value
 * decays with age. Both are fitted in `scripts/fit-dynasty.ts`; this module is
 * the runtime that consumes them.
 *
 * ## How it reaches a league-correct number
 *
 * The fitted curves emit *reference-scored* (half-PPR) points, which on their
 * own would misprice a superflex or TE-premium league exactly the way a curve
 * of half-PPR points would have broken the redraft engine (FR-21). So they are
 * never scored directly. Instead:
 *
 *     draft pick + career year
 *       → expected reference points          (DRAFT_CAPITAL_CURVES)
 *       → the positional rank that would produce those points   (inverted here)
 *       → a stat line                        (existing projectedStatLine)
 *       → points under the user's own scoring settings
 *
 * The middle step is the whole trick. Expressing the model's output as an
 * *effective positional rank* hands it straight to the redraft projection
 * pipeline, so every league-shape correction that pipeline already makes
 * applies to rookies for free — and the shipped coefficients stay at ~20 curves
 * instead of one set of stat curves per career year.
 *
 * Pure module — no I/O.
 */

import type { LeagueConfig, PlayerId, Position, StatLine } from './types.ts';
import { POSITION_CURVES } from './data/positionCurves.ts';
import {
  AGE_MULTIPLIERS,
  CAREER_YEARS,
  DRAFT_CAPITAL_CURVES,
  REFERENCE_SCORING,
  ROOKIE_PICK_VALUES,
} from './data/dynastyCurves.ts';
import { ROOKIE_CLASS_LATEST, rookieRecord } from './data/rookieClass.ts';
import { evaluateCurve, expandStatLine, projectedStatLine } from './projections.ts';
import { scoreStatLine } from './scoring.ts';

/**
 * Default annual discount applied to future seasons.
 *
 * Not a claim about interest rates. It prices the things that make a season
 * three years out worth less than next season regardless of talent: the league
 * may not exist, the roster will have turned over, and the projection itself is
 * far less certain that far out. 0.85 keeps year five worth roughly half of
 * year one, which is close to how dynasty markets actually discount.
 */
export const DEFAULT_DISCOUNT = 0.85;

/** Positions the dynasty model was fitted for. Kickers and defenses are not
 *  drafted in rookie drafts and have no meaningful aging curve. */
export const DYNASTY_POSITIONS: readonly Position[] = ['QB', 'RB', 'WR', 'TE'];

export function isDynastyPosition(position: Position | null): boolean {
  return position !== null && DYNASTY_POSITIONS.includes(position);
}

// ---------------------------------------------------------------------------
// Inverting points back into a positional rank
// ---------------------------------------------------------------------------

/**
 * Reference-scored points the fitted redraft curves imply at a positional rank.
 *
 * Deliberately computed from `POSITION_CURVES` rather than shipped as its own
 * table: it guarantees the inversion below is consistent with the very curves
 * it will be fed back into, so a refit of one cannot silently desynchronise
 * from the other.
 */
export function referencePointsAtRank(position: Position, rank: number): number {
  return scoreStatLine(REFERENCE_SCORING, projectedStatLine(position, rank));
}

/**
 * The positional rank whose projected stat line is worth `points` under the
 * reference scoring — the inverse of `referencePointsAtRank`.
 *
 * Binary search rather than algebra: the quantity being inverted is a sum of
 * Hill functions with different parameters, which has no closed form, but it is
 * monotonically decreasing in rank, which is all a bisection needs. Returns a
 * fractional rank so that small differences in expected points do not collapse
 * onto the same integer rank and flatten the top of the board.
 */
export function effectiveRankForPoints(position: Position, points: number): number {
  const curve = POSITION_CURVES[position];
  if (!curve) return 1;

  const best = referencePointsAtRank(position, 1);
  if (points >= best) return 1;

  const depth = curve.depth;
  const worst = referencePointsAtRank(position, depth);
  // Below the fitted depth the curve is flat, so there is no rank that produces
  // less. Clamping is honest; extrapolating past the data would not be.
  if (points <= worst) return depth;

  let lo = 1;
  let hi = depth;
  for (let i = 0; i < 40 && hi - lo > 0.01; i++) {
    const mid = (lo + hi) / 2;
    if (referencePointsAtRank(position, mid) > points) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

// ---------------------------------------------------------------------------
// Aging
// ---------------------------------------------------------------------------

/**
 * Value retained going from `age` into the following season.
 *
 * Ages outside the fitted range clamp to the nearest fitted age rather than
 * falling back to 1.0. A 37-year-old running back is not a player whose value
 * holds flat; he is past the end of the data, and the last fitted multiplier is
 * much closer to the truth than "no decay at all".
 */
export function ageMultiplier(position: Position, age: number): number {
  const table = AGE_MULTIPLIERS[position];
  if (!table) return 1;
  const ages = Object.keys(table).map(Number).sort((a, b) => a - b);
  if (ages.length === 0) return 1;

  const first = ages[0]!;
  const last = ages[ages.length - 1]!;
  const clamped = Math.min(Math.max(Math.round(age), first), last);
  return table[clamped] ?? 1;
}

/**
 * Cumulative share of a player's current value still expected `years` from now,
 * compounding the annual multipliers along the way.
 *
 * The multipliers carry regression to the mean as well as true aging (see the
 * fitter), so compounding them raw would understate a long hold badly. They are
 * therefore normalised against the position's own peak before compounding: what
 * survives is the *relative* aging shape, which is the part that is really
 * about age.
 */
export function agingRetention(position: Position, startAge: number, years: number): number {
  const table = AGE_MULTIPLIERS[position];
  if (!table || years <= 0) return 1;

  const peak = Math.max(...Object.values(table));
  if (!Number.isFinite(peak) || peak <= 0) return 1;

  let retained = 1;
  for (let i = 0; i < years; i++) {
    retained *= Math.min(1, ageMultiplier(position, startAge + i) / peak);
  }
  return retained;
}

/**
 * Scale every stat in a line by a constant.
 *
 * This exists because of a hard limit on the rank indirection: a positional
 * rank cannot express an expectation *below* what the deepest fitted rank
 * produces. The redraft curves stop at RB80 / WR100 / QB40 because nobody
 * drafts deeper than that, and a quarterback sitting at the 40th rank still
 * scores ~81 reference points — he is a backup who plays some. But the
 * draft-capital curve says a quarterback taken 180th expects ~12 points, and
 * the gap is not a worse rank, it is *probably never taking a snap*. There is
 * no rank that means that.
 *
 * Without this, every late-round rookie clamped to the deepest rank and got
 * priced as a fringe starter — quarterbacks taken 90th and 180th came out
 * identical at 81 points. Since scoring a stat line is linear in the stat
 * values, scaling the line and then scoring under the league's own settings is
 * exactly equivalent to scaling the league-scored total, so this costs nothing
 * in league-repricing fidelity (FR-21).
 */
function scaleStatLine(line: StatLine, factor: number): StatLine {
  if (factor >= 1) return line;
  const out: StatLine = {};
  for (const [key, value] of Object.entries(line)) {
    const scaled = value * factor;
    if (Math.abs(scaled) >= 0.005) out[key] = Math.round(scaled * 100) / 100;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Rookie projection
// ---------------------------------------------------------------------------

export interface RookieSeason {
  /** 1-based season of the player's career. */
  career_year: number;
  /** Expected points under the reference scoring the curves were fitted on. */
  reference_points: number;
  /** Positional rank those points correspond to. */
  effective_rank: number;
  stat_line: StatLine;
  /** Expected points under the user's league scoring. */
  league_points: number;
  /**
   * Fraction of the deepest fitted rank's production this season represents,
   * or 1 when the expectation lands on the fitted curve at all.
   *
   * Below 1 means "expected to be off the field more often than not" — see
   * `scaleStatLine` for why that cannot be expressed as a rank.
   */
  replacement_scale: number;
}

export interface RookieOutlook {
  position: Position;
  draft_pick: number;
  seasons: RookieSeason[];
  /** Discounted sum of `league_points` across the horizon. */
  dynasty_points: number;
  /** Mean R² of the underlying draft-capital curves — fit quality, not
   *  a claim about this particular player. */
  confidence: number;
}

export interface RookieProjectionOptions {
  /** Seasons to project. Defaults to the fitted career window. */
  horizon?: number;
  /** Annual discount on future seasons. */
  discount?: number;
}

/**
 * Project an incoming rookie across his first seasons, given where he was taken
 * in the *NFL* draft.
 *
 * `draftPick` is NFL draft position, not fantasy rookie-draft position — it is
 * the aggregated judgement of every professional scouting department and the
 * strongest signal available about a player with no professional snaps. The
 * curve it indexes already averages in every player taken at that slot who
 * never played, so the result is an expectation with bust risk priced in, not a
 * projection of the player's upside.
 */
export function projectRookie(
  position: Position,
  draftPick: number,
  league: LeagueConfig,
  options: RookieProjectionOptions = {},
): RookieOutlook | null {
  const curves = DRAFT_CAPITAL_CURVES[position];
  if (!curves) return null;

  const horizon = Math.max(1, Math.min(options.horizon ?? CAREER_YEARS, CAREER_YEARS));
  const discount = options.discount ?? DEFAULT_DISCOUNT;

  const seasons: RookieSeason[] = [];
  let dynastyPoints = 0;
  let r2Total = 0;
  let r2Count = 0;

  for (let year = 1; year <= horizon; year++) {
    const curve = curves[year];
    if (!curve) continue;

    const referencePts = Math.max(0, evaluateCurve(curve, Math.max(1, draftPick)));
    const rank = effectiveRankForPoints(position, referencePts);

    // Expectations below the deepest fitted rank's production are not a worse
    // rank, they are time off the field — scale the line instead of pinning it
    // to the floor. See `scaleStatLine`.
    const floor = referencePointsAtRank(position, rank);
    const scale = floor > 0 && referencePts < floor ? referencePts / floor : 1;

    const raw = scaleStatLine(projectedStatLine(position, rank), scale);
    const leaguePts = scoreStatLine(
      league.scoring_settings,
      expandStatLine(league.scoring_settings, raw),
    );

    seasons.push({
      career_year: year,
      reference_points: round2(referencePts),
      effective_rank: round2(rank),
      stat_line: raw,
      league_points: round2(leaguePts),
      replacement_scale: round2(scale),
    });

    dynastyPoints += leaguePts * discount ** (year - 1);
    r2Total += curve.r2;
    r2Count++;
  }

  if (seasons.length === 0) return null;

  return {
    position,
    draft_pick: draftPick,
    seasons,
    dynasty_points: round2(dynastyPoints),
    confidence: r2Count === 0 ? 0 : round2(r2Total / r2Count),
  };
}

/**
 * Discounted multi-year value of a player who is *already* established, using
 * this season's expected points and the aging curve.
 *
 * The complement to `projectRookie`: a rookie's future is driven by draft
 * capital because he has no history, while a veteran's is driven by his current
 * production and his age.
 */
export function projectVeteran(
  position: Position,
  age: number,
  currentSeasonPoints: number,
  options: RookieProjectionOptions = {},
): number {
  const horizon = Math.max(1, options.horizon ?? CAREER_YEARS);
  const discount = options.discount ?? DEFAULT_DISCOUNT;

  let total = 0;
  for (let year = 0; year < horizon; year++) {
    total += currentSeasonPoints * agingRetention(position, age, year) * discount ** year;
  }
  return round2(total);
}

// ---------------------------------------------------------------------------
// Looking a rookie up by Sleeper id
// ---------------------------------------------------------------------------

export interface RookieLookup {
  season: number;
  round: number;
  pick: number;
  /** Age at the NFL draft, where nflverse had it. */
  age: number | null;
}

/**
 * NFL draft capital for a Sleeper player, or null if he is not in the shipped
 * table.
 *
 * Null is a normal, expected answer rather than an error: the table covers the
 * two most recent draft classes, so every veteran misses it, as does any rookie
 * whose name could not be resolved with confidence and — until the table is
 * regenerated after the next NFL draft — the entire incoming class. Callers are
 * expected to degrade to ranking-source order rather than treat this as a
 * failure.
 */
export function draftCapitalFor(sleeperId: string): RookieLookup | null {
  const record = rookieRecord(sleeperId);
  if (!record) return null;
  const [season, round, pick, age] = record;
  return { season, round, pick, age };
}

/** True when the player is in the most recent shipped draft class. */
export function isIncomingRookie(sleeperId: string): boolean {
  return draftCapitalFor(sleeperId)?.season === ROOKIE_CLASS_LATEST;
}

/**
 * Project a player straight from his Sleeper id, resolving draft capital from
 * the shipped table. Returns null when he has none, which is the caller's cue
 * to fall back to the ordinary redraft path.
 */
export function projectRookieById(
  sleeperId: string,
  position: Position,
  league: LeagueConfig,
  options: RookieProjectionOptions = {},
): RookieOutlook | null {
  const capital = draftCapitalFor(sleeperId);
  if (!capital) return null;
  return projectRookie(position, capital.pick, league, options);
}

// ---------------------------------------------------------------------------
// Valuing a whole pool
// ---------------------------------------------------------------------------

/** How a player's dynasty number was arrived at, so the UI can say so. */
export type DynastyBasis =
  /** An incoming rookie, priced from where he went in the NFL draft. */
  | 'draft-capital'
  /** An established player, priced from this season's projection and his age. */
  | 'age-adjusted'
  /** Age unknown — this season's projection carried forward undecayed. */
  | 'no-age';

export interface DynastyValuation {
  player_key: PlayerId;
  dynasty_points: number;
  basis: DynastyBasis;
  age: number | null;
  capital: RookieLookup | null;
  /** 0-1. Fit quality for a rookie; a flat discount when age is missing. */
  confidence: number;
}

export interface DynastyPoolEntry {
  player_key: PlayerId;
  position: Position;
  /** Sleeper's age, where it has one. */
  age: number | null;
  /** This season's projected points under the league's scoring. */
  proj_points: number;
}

export interface DynastyPoolOptions extends RookieProjectionOptions {
  /**
   * Replacement points per position, from the engine's own baselines.
   *
   * Without this the board sorts on raw multi-year points, and in half-PPR that
   * means quarterbacks — a replacement-level quarterback outscores a good
   * running back, so every quarterback floats to the top and a 30-year-old
   * passer outranks a 24-year-old franchise back. Subtracting a positional
   * baseline is the same correction `orderByValue` already applies to the
   * redraft board, and the same one the rookie pick chart is built on.
   *
   * Omit it and values stay in raw points, which is occasionally what a caller
   * wants (a single player's projection in isolation) but never what a board
   * wants.
   */
  replacement?: Partial<Record<Position, number>>;
}

/**
 * Price every player in the pool on a dynasty basis.
 *
 * Two populations need genuinely different treatment, which is why this is not
 * one formula. An incoming rookie has no professional production to project
 * from, so he is priced off draft capital. Everyone else *does* have a
 * projection, and the dynasty question for them is not "how good is he" but
 * "how long does this last" — so their number is this season's expectation
 * carried forward through the aging curve.
 *
 * A player with no age at all still gets a number rather than being dropped:
 * Sleeper's `age` is missing for a good share of fringe players, and omitting
 * them would silently empty the bottom of the board. They are carried forward
 * undecayed and flagged `no-age` so the UI can mark the number as softer than
 * the others.
 */
export function valueDynastyPool(
  entries: readonly DynastyPoolEntry[],
  league: LeagueConfig,
  options: DynastyPoolOptions = {},
): Map<PlayerId, DynastyValuation> {
  const out = new Map<PlayerId, DynastyValuation>();
  const horizon = Math.max(1, options.horizon ?? CAREER_YEARS);
  const discount = options.discount ?? DEFAULT_DISCOUNT;
  const replacement = options.replacement;

  /**
   * Discounted value of a stream of seasons, each measured against the
   * position's replacement level.
   *
   * The baseline is subtracted *per season, before discounting*, rather than
   * once at the end — a player is only worth what he beats replacement by in
   * each individual year, and a season spent below replacement is worth zero
   * rather than a negative that a good year elsewhere could cancel out.
   */
  const above = (position: Position, seasonPoints: (year: number) => number): number => {
    const baseline = replacement?.[position] ?? 0;
    let total = 0;
    for (let year = 0; year < horizon; year++) {
      total += Math.max(0, seasonPoints(year) - baseline) * discount ** year;
    }
    return round2(total);
  };

  for (const entry of entries) {
    if (!isDynastyPosition(entry.position)) continue;

    const capital = draftCapitalFor(entry.player_key);
    const age = entry.age ?? capital?.age ?? null;

    // Rookies only: once a player has a season behind him his own projection
    // is strictly better information than where he was drafted.
    if (capital && capital.season === ROOKIE_CLASS_LATEST) {
      const outlook = projectRookie(entry.position, capital.pick, league, { horizon, discount });
      if (outlook) {
        out.set(entry.player_key, {
          player_key: entry.player_key,
          dynasty_points: above(
            entry.position,
            (year) => outlook.seasons[year]?.league_points ?? 0,
          ),
          basis: 'draft-capital',
          age,
          capital,
          confidence: outlook.confidence,
        });
        continue;
      }
    }

    if (age === null) {
      out.set(entry.player_key, {
        player_key: entry.player_key,
        dynasty_points: above(entry.position, () => entry.proj_points),
        basis: 'no-age',
        age: null,
        capital,
        confidence: 0.3,
      });
      continue;
    }

    out.set(entry.player_key, {
      player_key: entry.player_key,
      dynasty_points: above(
        entry.position,
        (year) => entry.proj_points * agingRetention(entry.position, age, year),
      ),
      basis: 'age-adjusted',
      age,
      capital,
      confidence: 0.7,
    });
  }

  return out;
}

/**
 * Order the board by dynasty value, mirroring `orderByValue` in
 * `valuation.ts` — available players sorted by the number, drafted players
 * left in their existing order behind them.
 *
 * Players the dynasty model cannot price at all (kickers, defenses) sort after
 * everyone it can, rather than being dropped from the board: a rookie draft
 * will not contain them, but a dynasty startup will, and silently removing
 * players from a board is worse than ranking them last.
 */
export function orderByDynasty(
  availableIds: readonly PlayerId[],
  draftedIdsInRankOrder: readonly PlayerId[],
  values: Map<PlayerId, DynastyValuation>,
): PlayerId[] {
  const ranked = [...availableIds].sort((a, b) => {
    const av = values.get(a)?.dynasty_points;
    const bv = values.get(b)?.dynasty_points;
    if (av === undefined && bv === undefined) return 0;
    if (av === undefined) return 1;
    if (bv === undefined) return -1;
    return bv - av;
  });
  return [...ranked, ...draftedIdsInRankOrder];
}

// ---------------------------------------------------------------------------
// Rookie draft picks as assets
// ---------------------------------------------------------------------------

/** Rookie draft slots the shipped chart covers. */
export const ROOKIE_PICK_SLOTS = ROOKIE_PICK_VALUES.length;

/**
 * Relative value of a rookie draft pick, normalised so the 1.01 is 1.0.
 *
 * `round` and `pickInRound` are 1-based, so the 2.03 is `rookiePickValue(2, 3)`.
 * `teams` defaults to 12 because the shipped chart was built on four rounds of
 * a 12-team draft; a different league size maps onto the same curve by overall
 * slot, which is the quantity the chart is actually indexed by.
 */
export function rookiePickValue(round: number, pickInRound: number, teams = 12): number {
  const slot = (round - 1) * teams + pickInRound;
  return rookiePickValueBySlot(slot);
}

/** As `rookiePickValue`, indexed by overall slot (1 = the 1.01). */
export function rookiePickValueBySlot(slot: number): number {
  if (slot < 1) return ROOKIE_PICK_VALUES[0] ?? 0;
  const idx = Math.min(Math.round(slot), ROOKIE_PICK_SLOTS) - 1;
  return ROOKIE_PICK_VALUES[idx] ?? 0;
}

/**
 * Label a rookie pick the way dynasty players write it — `1.01`, `2.07`.
 * Purely presentational, but it lives here so the slot arithmetic has exactly
 * one definition.
 */
export function rookiePickLabel(slot: number, teams = 12): string {
  const round = Math.floor((slot - 1) / teams) + 1;
  const inRound = ((slot - 1) % teams) + 1;
  return `${round}.${String(inRound).padStart(2, '0')}`;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
